import { Icon, token } from "@may-i/mash-design-system";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { useCurrentUser } from "~/features/auth/hooks/use-current-user";
import { useLogout } from "~/features/auth/hooks/use-logout";
import { LOCAL_STORAGE_AUTH_CHECK_KEY } from "~/shared/constants/auth";

export function UserMenu() {
  const { t, i18n } = useTranslation("shared");
  const location = useLocation();
  const { data: currentUser } = useCurrentUser();
  const { logout } = useLogout();
  const [isOpen, setIsOpen] = useState(false);

  const handleLogout = async () => {
    const currentPath = location.pathname + location.search;
    window.localStorage.setItem("redirectAfterLogin", currentPath);
    await logout();
    window.localStorage.removeItem(LOCAL_STORAGE_AUTH_CHECK_KEY);
    window.location.href = `/login?language=${i18n.language}`;
  };

  if (!currentUser) {
    return null;
  }

  return (
    <Container>
      <PrimaryLabel>{currentUser.isStaff ? "ADMIN" : currentUser.client.name}</PrimaryLabel>
      <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenu.Trigger asChild>
          <TriggerWrapper>
            <SecondaryLabel>
              {t("profile.username", { name: currentUser.name })}
            </SecondaryLabel>
            <ChevronIcon icon={isOpen ? "ic_chevron_up" : "ic_chevron_down"} width={16} />
          </TriggerWrapper>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <StyledContent sideOffset={9}>
            <StyledItem onClick={handleLogout}>{t("profile.menus.logout.label")}</StyledItem>
          </StyledContent>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </Container>
  );
}

const PrimaryLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  color: ${token("colors.text.primary")};
`;

const SecondaryLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  color: ${token("colors.text.secondary")};
`;

const Container = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  width: max-content;
  min-width: 160px;
  padding: 10px 16px;
  border-left: 1px solid ${token("colors.border.outline")};
`;

const TriggerWrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: 4px;
  cursor: pointer;
`;

const ChevronIcon = styled(Icon)`
  color: ${token("colors.text.secondary")};
`;

const StyledContent = styled(DropdownMenu.Content)`
  width: 200px;
  transform: translateX(-24px);
  background-color: ${token("colors.gray.white")};
  border: 1px solid ${token("colors.gray.GR300")};
  border-radius: 16px;
  padding: 8px 0;
  overflow: hidden;
  box-shadow: ${token("shadows.dropdown")};
`;

const StyledItem = styled(DropdownMenu.Item)`
  display: flex;
  align-items: center;
  padding: 0 12px;
  cursor: pointer;
  font-weight: ${token("font.weight.regular")};
  font-size: 12px;
  line-height: 18px;
  height: 28px;
  outline: none;
  background-color: ${token("colors.gray.white")};

  &:hover {
    background-color: ${token("colors.transparent.GR600.16")};
  }
`;
